import { useState } from "react";
import { fehlertext } from "../../kern/fehler";
import { anzeigename } from "../../kern/namen";
import { personNach, usePersonen } from "../../kern/personen";
import { useHinweis } from "../../kern/ui/Hinweise";
import { offeneUnteraufgaben, useAufgaben, useAufgabenAktionen } from "./daten";
import { lastHinweis, STATUS, type Aufgabe, type Status } from "./logik";
import { laufenderSprint, useSprints } from "./sprints";

/** Erledigen und Übernehmen mit Rückgängig (10 Sekunden) und der Rückfrage bei offenen Unteraufgaben. Löschen: loeschen.tsx
 *  Person hinzufügen und „In den Sprint“ mit Belastungshinweis wie beim Anlegen (BAUPLAN-ABSCHLUSS.md 4.7). */
export function useSchnellaktionen() {
  const akt = useAufgabenAktionen();
  const { zeigen } = useHinweis();
  const aufgaben = useAufgaben();
  const sprints = useSprints();
  const personen = usePersonen();
  const [rueckfrage, setRueckfrage] = useState<{ a: Aufgabe; text: string } | null>(null);

  const fehler = (e: unknown) => zeigen(fehlertext(e), { fehler: true });

  /** Hinweis, wenn eine der Personen damit mehr als fünf Aufgaben oder mehr als zwei mit Hoch im laufenden Sprint hätte. */
  function belastung(a: Pick<Aufgabe, "id" | "prioritaet">, pids: readonly string[]): string | null {
    const laufend = laufenderSprint(sprints.data);
    if (!laufend) return null;
    const andere = (aufgaben.data ?? []).filter((x) => x.id !== a.id);
    for (const pid of pids) {
      const p = personNach(personen.data, pid);
      const h = lastHinweis(andere, pid, laufend.id, a.prioritaet, p ? p.vorname || anzeigename(p) : "Diese Person");
      if (h) return h;
    }
    return null;
  }

  function personHinzu(a: Aufgabe, person: string) {
    const laufend = laufenderSprint(sprints.data);
    const hinweis = laufend && a.sprint_id === laufend.id ? belastung(a, [person]) : null;
    akt.personHinzu.mutate({ id: a.id, person }, { onSuccess: () => hinweis && zeigen(hinweis), onError: fehler });
  }

  /** `meldung`: Text nach dem Verschieben, der Belastungshinweis hängt sich an. `rueckgaengig`: zurück in den Backlog. */
  function inSprint(a: Aufgabe, meldung: string, rueckgaengig = false) {
    const hinweis = belastung(a, a.personen);
    akt.inSprint.mutate(a.id, {
      onSuccess: () =>
        zeigen(hinweis ? `${meldung} ${hinweis}` : meldung, rueckgaengig ? { rueckgaengig: () => akt.inBacklog.mutate(a.id, { onError: fehler }) } : undefined),
      onError: fehler,
    });
  }

  /** Nach jedem Wechsel ein Hinweis mit „Rückgängig“ (Erledigen und Wiederöffnen mit eigenem Text, sonst „„Titel“ ist jetzt In Arbeit.“,
   *  damit klar ist, wohin eine Aufgabe in den Gruppen nach Status gewandert ist, BAUPLAN-STATUS-IN-LISTEN.md Abschnitt 2). Er stellt den
   *  Status von vorher her, bei „Wartet“ mit Grund. Der neue Status steht sofort da, bei Fehler der alte. */
  function statusSetzen(a: Aufgabe, status: Status, grund?: string, unteraufgabenMit?: boolean) {
    const vorher = a.status;
    const vorherGrund = a.status_grund;
    const zurueck = () =>
      akt.statusSofort.mutate({ id: a.id, status: vorher, ...(vorher === "wartet" ? { grund: vorherGrund } : {}) }, { onError: fehler });
    akt.statusSofort.mutate(
      { id: a.id, status, ...(grund !== undefined ? { grund } : {}), ...(unteraufgabenMit !== undefined ? { unteraufgabenMit } : {}) },
      {
        onSuccess: () => {
          setRueckfrage(null);
          if (status === "erledigt") zeigen(`„${a.titel}“ erledigt.`, { rueckgaengig: zurueck });
          else if (vorher === "erledigt") zeigen(`„${a.titel}“ ist wieder offen.`, { rueckgaengig: zurueck });
          else if (status !== vorher) zeigen(`„${a.titel}“ ist jetzt ${STATUS[status]}.`, { rueckgaengig: zurueck });
        },
        onError: (e) => {
          const offen = offeneUnteraufgaben(e);
          if (offen) setRueckfrage({ a, text: offen });
          else fehler(e);
        },
      },
    );
  }

  return {
    akt,
    rueckfrage,
    rueckfrageSchliessen: () => setRueckfrage(null),
    rueckfrageAntwort: (mit: boolean) => rueckfrage && statusSetzen(rueckfrage.a, "erledigt", undefined, mit),
    statusSetzen,
    personHinzu,
    inSprint,
    erledigtUmschalten: (a: Aufgabe) => statusSetzen(a, a.status === "erledigt" ? "zu_tun" : "erledigt"),
    uebernehmen: (a: Aufgabe) =>
      akt.uebernehmen.mutate(a.id, {
        onSuccess: () => zeigen(`„${a.titel}“ liegt jetzt bei dir.`, { rueckgaengig: () => akt.abgeben.mutate(a.id, { onError: fehler }) }),
        onError: fehler,
      }),
    fehler,
  };
}
